import React from 'react';
import useSWR from 'swr';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import { useCurrentUser } from '@/lib/hooks/userData';
import joinNonEmptyParams from '@/utils/joinNonEmptyParams';
import { createApiRequest, fetcher } from '@/utils/services';

const useLabels = (searchParams, swrOptions) => {
  const ownerOrgId = useOwnerOrgId();
  const { user } = useCurrentUser();

  const key =
    searchParams && ownerOrgId
      ? `labels?${joinNonEmptyParams({
          ownerOrgId,
          ...searchParams,
        })}`
      : null;
  const swrResponse = useSWR(key, fetcher, swrOptions);
  const { data, mutate, ...restOfSwr } = swrResponse;

  // The `mutate` calls for `create`, `update`, and `remove` are doing a bit of
  // local state maintenance as Elastic Search results do not immediately
  // reflect the addition or removal of new labels.
  const create = React.useCallback(
    async ({ labelName, labelType }) => {
      const res = await createApiRequest({
        data: {
          createdBy: {
            displayName: user?.displayName,
            userId: user?.userId,
          },
          labelName,
          labelType,
          ownerOrgId,
        },
        method: 'post',
        path: 'labels',
      });
      const newLabel = res.data?.label;
      if (newLabel) {
        mutate(
          (oldVal) => {
            return {
              ...oldVal,
              totalHits: oldVal.totalHits + 1,
              labels: [newLabel, ...oldVal.labels],
            };
          },
          { revalidate: false },
        );
      }
      return newLabel;
    },
    [mutate, user],
  );

  const remove = async (labelId) => {
    await createApiRequest({
      method: 'delete',
      path: `labels/${labelId}`,
      throwErrors: true,
    });
    return mutate(
      (oldVal) => ({
        ...oldVal,
        totalHits: oldVal.totalHits - 1,
        labels: oldVal.labels.filter((label) => label.labelId !== labelId),
      }),
      { revalidate: false },
    );
  };

  const update = async (labelId, patch = {}) => {
    const res = await createApiRequest({
      data: {
        ownerOrgId,
        updatedBy: {
          displayName: user?.displayName,
          userId: user?.userId,
        },
        ...patch,
      },
      method: 'put',
      path: `labels/${labelId}`,
      throwErrors: true,
    });
    const newLabel = res.data?.label;
    if (newLabel) {
      await mutate(
        (oldVal) => {
          const oldLabels = oldVal.labels;
          const newLabels = oldLabels.map((label) =>
            label.labelId === labelId ? newLabel : label,
          );
          return { ...oldVal, labels: newLabels };
        },
        { revalidate: false },
      );
    }
    return newLabel;
  };

  return {
    create,
    data: data?.labels,
    remove,
    total: data?.totalHits || 0,
    update,
    ...restOfSwr,
  };
};

export default useLabels;
